import { MARKET_CONTENT } from "./i18n/market-content";
import pagesptBR from "./marketing-pages.pt-BR.json";
import pagestr from "./marketing-pages.tr.json";
import pagesvi from "./marketing-pages.vi.json";
import pagesid from "./marketing-pages.id.json";
import pagesit from "./marketing-pages.it.json";
import pagesth from "./marketing-pages.th.json";
import pagesuk from "./marketing-pages.uk.json";
import pagesnl from "./marketing-pages.nl.json";
// Public feature and free tool pages: route list, search metadata and schema.org data.
// Page bodies live in web/src/marketing-content.ts (+ .en.ts, .es.ts); the sitemap reads the same JSON.
// Articles (教學專欄, D-270) are not here: they live in content/articles and are served by src/articles/, gated by
// their publish time; the JSON keeps only articlesIndex (path, name and the search title of /articles).
// Each locale has its own JSON with identical slugs (marketing-pages.en.json for English);
// paths in the JSON are base paths, the locale prefix (/en) is added by localizePath.
import pages from "./marketing-pages.json";
import pagesZhHans from "./marketing-pages.zh-hans.json";
import pagesEn from "./marketing-pages.en.json";
import pagesEs from "./marketing-pages.es.json";
import pagesFr from "./marketing-pages.fr.json";
import { DEFAULT_LOCALE, LOCALE_CONFIG, localizePath, type Locale } from "./i18n/locales";
import { TRADITIONAL_ONLY_TOOLS } from "./traditional-only";

const ORIGIN = "https://classmori.com";

export type MarketingFeature = (typeof pages.features)[number];
export type MarketingTool = (typeof pages.tools)[number];
export type MarketingPages = typeof pages;

const PAGES: Record<Locale, MarketingPages> = {
  "pt-BR": pagesptBR as MarketingPages,
  "tr": pagestr as MarketingPages,
  "vi": pagesvi as MarketingPages,
  "id": pagesid as MarketingPages,
  "it": pagesit as MarketingPages,
  "th": pagesth as MarketingPages,
  "uk": pagesuk as MarketingPages,
  "nl": pagesnl as MarketingPages,
  "zh-Hant": pages,
  "zh-Hans": pagesZhHans as MarketingPages,
  en: pagesEn as MarketingPages,
  es: pagesEs as MarketingPages,
  fr: pagesFr as MarketingPages,
};
export const marketingPagesFor = (locale: Locale): MarketingPages => PAGES[locale];
/** The Traditional Chinese pages without its Taiwan-only tools: the shape every other locale must match. */
export const sharedMarketingPages = (): MarketingPages => ({ ...pages, tools: pages.tools.filter((tool) => !TRADITIONAL_ONLY_TOOLS.has(tool.slug)) });
/** Default-locale (Traditional Chinese) pages; kept for existing callers. */
export const marketingPages = pages;
export const featurePath = (slug: string) => `${pages.featuresIndex.path}/${slug}`;
export const articlePath = (slug: string) => `${pages.articlesIndex.path}/${slug}`;
export const toolPath = (slug: string) => `${pages.toolsIndex.path}/${slug}`;

function metadataFor(set: MarketingPages): Record<string, { title: string; description: string }> {
  return Object.fromEntries([
    // A light language (D-231) has tool pages only: no feature or article index to publish.
    ...(set.features.length ? [[set.featuresIndex.path, { title: set.featuresIndex.title, description: set.featuresIndex.description }]] : []),
    ...set.features.map((feature) => [featurePath(feature.slug), { title: feature.title, description: feature.description }]),
    [set.toolsIndex.path, { title: set.toolsIndex.title, description: set.toolsIndex.description }],
    ...set.tools.map((tool) => [toolPath(tool.slug), { title: tool.title, description: tool.description }]),
  ]);
}
const METADATA = Object.fromEntries(Object.entries(PAGES).map(([locale, set]) => [locale, metadataFor(set)])) as Record<Locale, Record<string, { title: string; description: string }>>;
/** Search metadata keyed by base path (no /en prefix). */
export const marketingPageMetadataFor = (locale: Locale) => METADATA[locale];
export const marketingPageMetadata = METADATA[DEFAULT_LOCALE];

/** Free no-login tools (/tools and one slug below it). */
export const isToolsPrefixPath = (path: string) => path === pages.toolsIndex.path || path.startsWith(`${pages.toolsIndex.path}/`);

/** Paths under /features, /articles and /tools are public pages; unknown ones get an unindexed 404. */
export const isMarketingPrefixPath = (path: string) =>
  path === pages.featuresIndex.path || path.startsWith(`${pages.featuresIndex.path}/`)
  || path === pages.articlesIndex.path || path.startsWith(`${pages.articlesIndex.path}/`)
  || isToolsPrefixPath(path);

/** /articles and one slug below it (教學專欄, D-270), without a locale prefix. */
export const isArticlesPrefixPath = (path: string) => path === pages.articlesIndex.path || /^\/articles\/[^/]+$/.test(path);

// Breadcrumb and tool-name words for schema.org; locales without an entry use English.
const JSON_LD_WORDS: Partial<Record<Locale, { home: string; freeTool: string }>> = {
  "pt-BR": MARKET_CONTENT["pt-BR"].schema,
  "tr": MARKET_CONTENT["tr"].schema,
  "vi": MARKET_CONTENT["vi"].schema,
  "id": MARKET_CONTENT["id"].schema,
  /* i18n-ignore: static Gemini-translated copy */ "zh-Hans": { home: "首页", freeTool: "免费工具" },
  "zh-Hant": { home: "首頁", freeTool: "免費工具" }, // i18n-ignore
  en: { home: "Home", freeTool: "free tool" },
  it: { home: "Home", freeTool: "strumento gratuito" },
  th: { home: "หน้าแรก", freeTool: "เครื่องมือฟรี" },
  uk: { home: "Головна", freeTool: "безкоштовний інструмент" },
  nl: { home: "Home", freeTool: "gratis tool" },
  es: { home: "Inicio", freeTool: "herramienta gratuita" },
  fr: { home: "Accueil", freeTool: "outil gratuit" },
};

export function jsonLdParts(locale: Locale) {
  const config = LOCALE_CONFIG[locale];
  const set = PAGES[locale];
  const meta = METADATA[locale];
  const url = (path: string) => `${ORIGIN}${localizePath(path, locale)}`;
  const siteName = config.siteName;
  const organization = { "@type": "Organization", "@id": `${ORIGIN}/#org`, name: LOCALE_CONFIG[DEFAULT_LOCALE].siteName, alternateName: "ClassMori", url: `${ORIGIN}/`, logo: `${ORIGIN}/assets/forest-ui/brand/icon-192.png` };
  const website = { "@type": "WebSite", "@id": `${url("/")}#site`, url: url("/"), name: siteName, inLanguage: config.schemaLanguage, publisher: { "@id": `${ORIGIN}/#org` } };
  const homeName = JSON_LD_WORDS[locale]?.home ?? JSON_LD_WORDS.en!.home;
  const breadcrumb = (trail: Array<[string, string]>) => ({
    "@type": "BreadcrumbList",
    itemListElement: [[homeName, "/"] as [string, string], ...trail].map(([name, path], index) => ({ "@type": "ListItem", position: index + 1, name, item: url(path) })),
  });
  const itemList = (items: Array<{ name: string; path: string }>) => ({
    "@type": "ItemList",
    itemListElement: items.map((item, index) => ({ "@type": "ListItem", position: index + 1, name: item.name, url: url(item.path) })),
  });
  const webPage = (path: string, type: "WebPage" | "CollectionPage", extra: Record<string, unknown> = {}) => ({
    "@type": type, "@id": `${url(path)}#page`, url: url(path), name: meta[path].title,
    description: meta[path].description, inLanguage: config.schemaLanguage, isPartOf: { "@id": `${url("/")}#site` }, ...extra,
  });
  return { config, set, url, siteName, organization, website, breadcrumb, itemList, webPage };
}

/** schema.org graph for a marketing page; `path` is the base path (no locale prefix). */
export function marketingJsonLd(path: string, locale: Locale = DEFAULT_LOCALE): unknown | null {
  const { config, set: pages, url, siteName: SITE_NAME, organization, website, breadcrumb, itemList, webPage } = jsonLdParts(locale);
  const freeTool = JSON_LD_WORDS[locale]?.freeTool ?? JSON_LD_WORDS.en!.freeTool;
  const graph = (...nodes: unknown[]) => ({ "@context": "https://schema.org", "@graph": [organization, website, ...nodes] });
  if (path === pages.featuresIndex.path)
    return graph(webPage(path, "CollectionPage", { mainEntity: itemList(pages.features.map((feature) => ({ name: feature.name, path: featurePath(feature.slug) }))) }), breadcrumb([[pages.featuresIndex.name, path]]));
  const feature = pages.features.find((item) => featurePath(item.slug) === path);
  if (feature)
    return graph(webPage(path, "WebPage", { about: { "@type": "SoftwareApplication", name: SITE_NAME, applicationCategory: "EducationalApplication", operatingSystem: "Web", url: url("/") } }), breadcrumb([[pages.featuresIndex.name, pages.featuresIndex.path], [feature.name, path]]));
  if (path === pages.toolsIndex.path)
    return graph(webPage(path, "CollectionPage", { mainEntity: itemList(pages.tools.map((tool) => ({ name: tool.name, path: toolPath(tool.slug) }))) }), breadcrumb([[pages.toolsIndex.name, pages.toolsIndex.path]]));
  const tool = pages.tools.find((item) => toolPath(item.slug) === path);
  if (tool)
    return graph({
      "@type": "WebApplication", "@id": `${url(path)}#app`, name: locale === DEFAULT_LOCALE ? `${tool.name}｜${SITE_NAME}${freeTool}` : `${tool.name} | ${SITE_NAME} ${freeTool}`, url: url(path),
      ...("alternateNames" in tool && Array.isArray(tool.alternateNames) ? { alternateName: tool.alternateNames } : {}),
      description: tool.description, image: `${ORIGIN}/assets/forest-ui/marketing/tool-${tool.slug}-v1.webp`,
      applicationCategory: "EducationalApplication", operatingSystem: "Web", browserRequirements: "Requires JavaScript",
      inLanguage: config.schemaLanguage, isAccessibleForFree: true, publisher: { "@id": `${ORIGIN}/#org` },
      offers: { "@type": "Offer", price: "0", priceCurrency: config.currency },
    }, breadcrumb([[pages.toolsIndex.name, pages.toolsIndex.path], [tool.name, path]]));
  return null;
}
